import React from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AllInclusiveRoundedIcon from "@mui/icons-material/AllInclusiveRounded";
import { useTranslation } from "react-i18next";
import DashboardCachesButton from "./DashboardCachesButton";
import DashboardShareButton from "./DashboardShareButton";

const dateFieldSx = {
  width: 148,
  flexShrink: 0,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: 2,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.875rem",
    py: 1,
  },
};

/**
 * Toolbar em grid de 3 colunas:
 * [Período] | [Filtros] | [Ações]
 */
export default function DashboardPageToolbar({
  title,
  showPeriod = true,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  maxSelectableDate,
  isMobile = false,
  filterSlot = null,
  periodDisabled = false,
  periodDisabledHint = null,
  onPeriodChipClick = null,
  showCaches = false,
  onCachesOpen,
  showShare = false,
  onShareOpen,
  showExport = false,
  onExport,
  exportDisabled = false,
}) {
  const { t } = useTranslation("settings");
  const theme = useTheme();

  const hasActions = showCaches || showShare || showExport;
  const hasToolbarContent = showPeriod || Boolean(filterSlot) || hasActions;

  const periodInner = showPeriod ? (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: "nowrap", minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ color: "text.secondary", flexShrink: 0 }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 650, letterSpacing: 0.15, whiteSpace: "nowrap", fontSize: "0.75rem" }}
        >
          {t("dashboardPage.periodLabel", { defaultValue: "Período" })}
        </Typography>
      </Stack>

      {periodDisabled ? (
        <Tooltip
          title={
            periodDisabledHint ||
            t("dashboardPage.filters.chipAllPeriodTooltip", {
              defaultValue: "Inclui todas as solicitações em aberto, sem limite de data",
            })
          }
        >
          <Chip
            size="small"
            icon={<AllInclusiveRoundedIcon sx={{ fontSize: "16px !important" }} />}
            color="primary"
            variant="filled"
            clickable={typeof onPeriodChipClick === "function"}
            onClick={onPeriodChipClick || undefined}
            label={t("dashboardPage.filters.chipAllPeriodLong", {
              defaultValue: "Todo o histórico em aberto",
            })}
            sx={{
              height: 32,
              fontWeight: 600,
              fontSize: "0.75rem",
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
              "& .MuiChip-icon": { color: "primary.main" },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.18),
              },
            }}
          />
        </Tooltip>
      ) : (
        <>
          <DatePicker
            label={t("dashboardPage.startDate")}
            value={startDate}
            onChange={onStartDateChange}
            format="DD/MM/YYYY"
            maxDate={maxSelectableDate}
            slotProps={{
              textField: {
                size: "small",
                sx: dateFieldSx,
              },
            }}
          />
          <ArrowForwardIcon sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }} />
          <DatePicker
            label={t("dashboardPage.endDate")}
            value={endDate}
            onChange={onEndDateChange}
            format="DD/MM/YYYY"
            maxDate={maxSelectableDate}
            slotProps={{
              textField: {
                size: "small",
                sx: dateFieldSx,
              },
            }}
          />
        </>
      )}
    </Stack>
  ) : null;

  const actionButtons = hasActions ? (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
      {(showCaches || showShare) ? (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <DashboardCachesButton visible={showCaches} onOpen={onCachesOpen} />
          <DashboardShareButton visible={showShare} onOpen={onShareOpen} />
        </Stack>
      ) : null}
      {showExport ? (
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          onClick={onExport}
          disabled={exportDisabled}
          sx={{
            whiteSpace: "nowrap",
            px: 2,
            height: 36,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        >
          {t("dashboardPage.export")}
        </Button>
      ) : null}
    </Stack>
  ) : null;

  const desktopColumns = (() => {
    if (showPeriod && filterSlot && hasActions) return "auto minmax(240px, 1fr) auto";
    if (showPeriod && filterSlot) return "auto minmax(240px, 1fr)";
    if (filterSlot && hasActions) return "minmax(0, 1fr) auto";
    if (showPeriod && hasActions) return "1fr auto";
    return "1fr";
  })();

  return (
    <Box className="tour-dashboard-header" sx={{ mb: 2 }}>
      {title ? (
        <Typography
          variant="h4"
          component="h1"
          sx={{
            mb: 1.5,
            fontWeight: 700,
            fontSize: { xs: "1.5rem", sm: "1.75rem" },
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </Typography>
      ) : null}

      {hasToolbarContent ? (
        <Paper
          className="tour-dashboard-period"
          variant="outlined"
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.25, sm: 1.35 },
            borderRadius: 2.5,
            borderColor: alpha(theme.palette.divider, 0.9),
            backgroundColor: "background.paper",
            overflow: "hidden",
            boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              columnGap: 0,
              rowGap: 1.5,
              gridTemplateColumns: {
                xs: "1fr",
                md: isMobile ? "1fr" : desktopColumns,
              },
            }}
          >
            {periodInner ? (
              <Box
                sx={{
                  minWidth: 0,
                  pr: { md: filterSlot || hasActions ? 2 : 0 },
                  mr: { md: filterSlot || (!isMobile && hasActions) ? 2 : 0 },
                  borderRight: {
                    md: filterSlot || (!isMobile && hasActions) ? "1px solid" : "none",
                  },
                  borderColor: { md: "divider" },
                }}
              >
                {periodInner}
              </Box>
            ) : null}

            {filterSlot ? (
              <Box
                sx={{
                  minWidth: 0,
                  width: "100%",
                  pr: { md: !isMobile && hasActions ? 2 : 0 },
                  mr: { md: !isMobile && hasActions ? 2 : 0 },
                  borderRight: {
                    md: !isMobile && hasActions ? "1px solid" : "none",
                  },
                  borderColor: { md: "divider" },
                }}
              >
                {filterSlot}
              </Box>
            ) : null}

            {!isMobile && actionButtons ? (
              <Box sx={{ justifySelf: "end", minWidth: 0 }}>{actionButtons}</Box>
            ) : null}
          </Box>

          {isMobile && actionButtons ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              {actionButtons}
            </>
          ) : null}
        </Paper>
      ) : null}
    </Box>
  );
}
